<script setup lang="ts">
import { toRefs, Ref, ref } from "vue";
import { useI18n } from "vue-i18n";
import { titleInputRules } from "@/ui/validation/validationRules";
import SegmentDO = App.Data.Segments.SegmentDO;
import { useApiClient } from "@/api/client";
import { useSegmentBuilderStore } from "@/modules/segments/store";

const isDialogVisible = defineModel<boolean>();
const { t } = useI18n();

const props = defineProps<{
	segmentToSave: SegmentDO;
	dependsOnSegmentId?: number | null;
	dependsOnSegmentNameClean?:
		| "segmentcustomer"
		| "segmentlostcustomer"
		| "segmentreservation"
		| null;
	onSuccess: Function;
	isEditing?: boolean;
	showCohortMonthSelection?: boolean;
}>();

const { segmentToSave, dependsOnSegmentId, dependsOnSegmentNameClean } =
	toRefs(props);

const isFormValid: Ref<boolean> = ref(false);
const cohortNoOfMonths = ref(12);
const isSaving = ref(false);

const saveSegment = async () => {
	isSaving.value = true;
	const response =
		props.isEditing === true
			? await useApiClient("segments").softUpdate(segmentToSave.value)
			: await useApiClient("segments").create(
					segmentToSave.value,
					dependsOnSegmentId.value,
					dependsOnSegmentNameClean.value,
					cohortNoOfMonths.value
			  );

	if (response.isOk()) {
		useSegmentBuilderStore().segmentBuilderState.segmentHasChanges = false;
		isDialogVisible.value = false;
		props.onSuccess(response.value);
	}
	isSaving.value = false;
};
</script>

<template>
	<v-dialog v-model="isDialogVisible" persistent width="60vw">
		<v-form v-model="isFormValid">
			<v-card elevation="9" class="help">
				<v-toolbar>
					<v-toolbar-title>
						<i class="fa-duotone fa-object-intersect fa-xl mr-3" />
						{{ props.isEditing ? "Update Segment" : "Save Segment as..." }}
					</v-toolbar-title>
					<v-spacer />

					<v-btn icon @click="isDialogVisible = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-toolbar>

				<v-divider style="border-color: unset !important" />
				<div class="pa-5">
					<div class="pa-2">
						<h6 class="text-h6 mb-3">Title:</h6>
						<v-text-field
							hide-details="auto"
							:min-width="200"
							variant="filled"
							placeholder="Title name"
							v-model="segmentToSave.title"
							:rules="titleInputRules(t)"
						/>
					</div>

					<div class="pa-2">
						<div>
							<h6 class="text-h6 mb-3">Type (output):</h6>
							<v-select
								:disabled="typeof dependsOnSegmentId === 'number' || isEditing"
								hide-details="auto"
								variant="filled"
								:items="['customer', 'reservation']"
								v-model="segmentToSave.type"
							/>
						</div>
					</div>

					<div class="pa-2">
						<div>
							<h6 class="text-h6 mb-3">Description:</h6>
							<v-textarea
								clearable
								hide-details="auto"
								v-model="segmentToSave.description"
								placeholder="Description"
								variant="filled"
							/>
						</div>
					</div>

					<div class="pa-2" v-if="showCohortMonthSelection">
						<div>
							<h6 class="text-h6 mb-3">Months to look back:</h6>
							<v-autocomplete
								hide-details="auto"
								placeholder="Select a Value"
								:items="Array.from({ length: 12 }, (_, i) => i + 1)"
								v-model="cohortNoOfMonths"
								:clearable="false"
							></v-autocomplete>
						</div>
					</div>
				</div>
				<v-divider style="border-color: unset !important"></v-divider>

				<v-card-actions class="mx-5 my-2">
					<v-spacer />
					<v-btn text="Cancel" @click="isDialogVisible = false"></v-btn>
					<v-btn
						text="Save"
						variant="flat"
						:disabled="isFormValid === false || isSaving === true"
						@click="saveSegment()"
					></v-btn>
				</v-card-actions>
			</v-card>
		</v-form>
	</v-dialog>
</template>

<style lang="scss"></style>
